import { DatePicker, Radio, Space } from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs from "@/utils/time";
import type { Dayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import { useIntl } from "@umijs/max";
import { ProForm } from "@ant-design/pro-components";

const { RangePicker } = DatePicker;

export type DateRangeValue = [Dayjs | null, Dayjs | null] | null;

interface DateRangeWithRadioProps {
  value?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
  maxDays?: number; // 最大选择天数限制，默认 180 (半年)
}

const DateRangeWithRadio: React.FC<DateRangeWithRadioProps> = ({
  value,
  onChange,
  maxDays = 180,
}) => {
  const intl = useIntl();
  const form = ProForm.useFormInstance();
  const [radioValue, setRadioValue] = useState<string>("");

  // 定义日期范围选项
  const options = [
    {
      label: intl.formatMessage({
        id: "pages.common.date.preset.all",
      }),
      value: "all",
      range: () => null,
    },
    {
      label: intl.formatMessage({ id: "pages.common.date.preset.today" }),
      value: "today",
      range: () => [dayjs().startOf("day"), dayjs().endOf("day")],
    },
    {
      label: intl.formatMessage({ id: "pages.common.date.preset.yesterday" }),
      value: "yesterday",
      range: () => [
        dayjs().subtract(1, "day").startOf("day"),
        dayjs().subtract(1, "day").endOf("day"),
      ],
    },
    {
      label: intl.formatMessage({ id: "pages.common.date.preset.last7Days" }),
      value: "last7",
      range: () => [
        dayjs().subtract(6, "day").startOf("day"),
        dayjs().endOf("day"),
      ],
    },
    {
      label: intl.formatMessage({
        id: "pages.common.date.preset.last15Days",
      }),
      value: "last15",
      range: () => [
        dayjs().subtract(14, "day").startOf("day"),
        dayjs().endOf("day"),
      ],
    },
    {
      label: intl.formatMessage({ id: "pages.common.date.preset.last30Days" }),
      value: "last30",
      range: () => [
        dayjs().subtract(29, "day").startOf("day"),
        dayjs().endOf("day"),
      ],
    },
  ];

  const [dates, setDates] = useState<any>(null);

  // 监听外部 value 变化，同步 radioValue
  useEffect(() => {
    if (!value || !value[0] || !value[1]) {
      setRadioValue("all");
      return;
    }

    const matchedOption = options.find((opt) => {
      const range = opt.range();
      if (!range) return false;
      const [start, end] = range;
      return value[0]?.isSame(start, "day") && value[1]?.isSame(end, "day");
    });

    setRadioValue(matchedOption ? matchedOption.value : "custom");
  }, [value]);

  const handleRadioChange = (e: any) => {
    const val = e.target.value;
    const option = options.find((opt) => opt.value === val);
    if (option) {
      const range = option.range();
      onChange?.(range as DateRangeValue);
      // 让表单立即提交
      setTimeout(() => {
        form?.submit();
      }, 0);
    }
  };

  const handleRangeChange = (dates: any) => {
    onChange?.(dates);
  };

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    if (!current) return false;

    // 1. 禁止选择未来时间
    const isFuture = current > dayjs().endOf("day");
    if (isFuture) return true;

    // 2. 如果已经选了一个日期，限制另一个日期的跨度在 maxDays 内
    if (dates && dates[0] && !dates[1]) {
      const tooFarNext = current.diff(dates[0], "days") > maxDays - 1;
      const tooFarPrev = dates[0].diff(current, "days") > maxDays - 1;
      return tooFarNext || tooFarPrev;
    }

    return false;
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
      }}
      className="date-range-with-radio"
    >
      <Radio.Group
        value={radioValue}
        onChange={handleRadioChange}
        optionType="button"
        buttonStyle="solid"
        style={{ flexShrink: 0, whiteSpace: "nowrap" }}
      >
        {options.map((opt) => (
          <Radio.Button key={opt.value} value={opt.value}>
            {opt.label}
          </Radio.Button>
        ))}
      </Radio.Group>
      <RangePicker
        value={value as any}
        onChange={handleRangeChange}
        onCalendarChange={(val) => setDates(val)}
        onOpenChange={(open) => {
          if (!open) {
            setDates(null);
          }
        }}
        disabledDate={disabledDate}
        style={{ flex: 1 }}
      />
    </div>
  );
};

export default DateRangeWithRadio;
